import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components';
import {
  createWhatsAppAppChannelAccount,
  getWhatsAppAppSession,
  getWhatsAppAppStatus,
  logoutWhatsAppApp,
  refreshWhatsAppAppQr,
} from '../services/channelAccountService';
import { WhatsAppAppSession } from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';

interface WhatsAppAppConnectorTabProps {
  canManage: boolean;
}

const POLL_MS = 2000;

const qrImageSrc = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
};

export const WhatsAppAppConnectorTab: React.FC<WhatsAppAppConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const accounts = useChannelAccounts('WhatsApp', canManage, 'app');

  const [platformConfigured, setPlatformConfigured] = React.useState<boolean | null>(null);
  const [session, setSession] = React.useState<WhatsAppAppSession | null>(null);
  const [loadingSession, setLoadingSession] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [displayName, setDisplayName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [refreshingQr, setRefreshingQr] = React.useState(false);

  React.useEffect(() => {
    if (!canManage) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const status = await getWhatsAppAppStatus();
        if (!cancelled) {
          setPlatformConfigured(status.configured);
        }
      } catch {
        if (!cancelled) {
          setPlatformConfigured(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const loadSession = React.useCallback(async (accountId: string) => {
    setLoadingSession(true);
    setError(null);
    try {
      const next = await getWhatsAppAppSession(accountId);
      setSession(next);
      return next;
    } catch (loadError) {
      setSession(null);
      setError(loadError instanceof Error ? loadError.message : t('failedToLoadWhatsAppAppSession', { ns: 'companies' }));
      return null;
    } finally {
      setLoadingSession(false);
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      setSession(null);
      return;
    }

    void loadSession(accountId);
  }, [accounts.selectedAccountId, loadSession]);

  React.useEffect(() => {
    const accountId = accounts.selectedAccountId;
    const status = session?.connectionStatus?.toLowerCase();
    const shouldPoll = Boolean(accountId && status && status !== 'open' && status !== 'close');
    if (!shouldPoll || !accountId) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadSession(accountId);
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [accounts.selectedAccountId, loadSession, session?.connectionStatus]);

  const createAccount = async () => {
    const name = displayName.trim();
    if (!name) {
      const message = t('whatsappAppDisplayNameRequired', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
      return;
    }

    setCreating(true);
    setError(null);
    setNotice(null);

    try {
      const created = await createWhatsAppAppChannelAccount({ displayName: name, isDefault: accounts.accounts.length === 0 });
      setSession(created);
      setDisplayName('');
      setShowAddForm(false);
      await accounts.reload();
      accounts.setSelectedAccountId(created.account.id);
      const message = t('whatsappAppAccountCreated', { ns: 'companies' });
      setNotice(message);
      showToast(message, 'success');
    } catch (createError) {
      const message = createError instanceof Error ? createError.message : t('failedToCreateWhatsAppAppAccount', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const reconnect = async () => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      return;
    }

    setRefreshingQr(true);
    setError(null);
    setNotice(null);
    try {
      const next = await refreshWhatsAppAppQr(accountId);
      setSession(next);
    } catch (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : t('failedToRefreshWhatsAppAppQr', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setRefreshingQr(false);
    }
  };

  const disconnect = async () => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      return;
    }

    setDisconnecting(true);
    setError(null);
    setNotice(null);
    try {
      const next = await logoutWhatsAppApp(accountId);
      setSession(next);
      await accounts.reload();
      const message = t('whatsappAppDisconnected', { ns: 'companies' });
      setNotice(message);
      showToast(message, 'success');
    } catch (logoutError) {
      const message = logoutError instanceof Error ? logoutError.message : t('failedToDisconnectWhatsAppApp', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!canManage) {
    return null;
  }

  const connected = session?.connectionStatus?.toLowerCase() === 'open';
  const qrSrc = qrImageSrc(session?.qrCodeBase64);
  const statusLabel = t(`whatsappAppStatus.${session?.connectionStatus ?? 'connecting'}`, {
    ns: 'companies',
    defaultValue: session?.connectionStatus ?? t('whatsappAppStatus.connecting', { ns: 'companies' }),
  });

  return (
    <section className="company-section" style={{ marginTop: 8 }}>
      <ChannelAccountBar
        channel="WhatsApp"
        connectionKind="app"
        accounts={accounts.accounts}
        limits={accounts.limits}
        loading={accounts.loading}
        error={accounts.error}
        selectedAccountId={accounts.selectedAccountId}
        onSelectAccount={accounts.setSelectedAccountId}
        onRefresh={() => { void accounts.reload(); }}
        canManage={canManage}
        showAddForm={showAddForm}
        onToggleAddForm={() => setShowAddForm(current => !current)}
        onAccountUpdated={() => { void accounts.reload(); }}
      />

      {showAddForm ? (
        <div className="company-card" style={{ marginTop: 16 }}>
          <h4>{t('whatsappAppAddAccountHeading', { ns: 'companies' })}</h4>
          <label>
            {t('whatsappAppDisplayName', { ns: 'companies' })}
            <input
              className="text-input"
              value={displayName}
              onChange={event => setDisplayName(event.target.value)}
              disabled={creating}
            />
          </label>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={() => { void createAccount(); }} disabled={creating}>
              {creating ? t('savingEllipsis', { ns: 'admin' }) : t('whatsappAppCreateAccount', { ns: 'companies' })}
            </button>
          </div>
        </div>
      ) : null}

      {platformConfigured === false ? (
        <div className="error" style={{ marginTop: 16 }}>{t('whatsappAppNotConfigured', { ns: 'companies' })}</div>
      ) : null}

      {error ? <div className="error" style={{ marginTop: 16 }}>{error}</div> : null}
      {notice ? <div className="success" style={{ marginTop: 16 }}>{notice}</div> : null}

      {accounts.selectedAccountId ? (
        <div className="company-card" style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(220px, 280px)', gap: 24 }}>
          <div>
            <h4>{t('whatsappAppSessionHeading', { ns: 'companies' })}</h4>
            <p style={{ color: '#516173', fontSize: '0.95rem' }}>{t('whatsappAppInstructions', { ns: 'companies' })}</p>
            <p style={{ color: '#8a5a00', fontSize: '0.9rem' }}>{t('whatsappAppWarning', { ns: 'companies' })}</p>
            {loadingSession && !session ? (
              <Spinner text={t('loadingWhatsAppAppSession', { ns: 'companies' })} />
            ) : (
              <>
                <p><strong>{t('whatsappAppConnectionStatus', { ns: 'companies' })}:</strong> {statusLabel}</p>
                {session?.linkedPhoneNumber ? (
                  <p><strong>{t('whatsappAppLinkedPhone', { ns: 'companies' })}:</strong> {session.linkedPhoneNumber}</p>
                ) : null}
                {session?.lastQrAtUtc ? (
                  <p><strong>{t('whatsappAppLastQr', { ns: 'companies' })}:</strong> {new Date(session.lastQrAtUtc).toLocaleString()}</p>
                ) : null}
                {session?.lastDisconnectReason ? (
                  <p><strong>{t('whatsappAppLastDisconnect', { ns: 'companies' })}:</strong> {session.lastDisconnectReason}</p>
                ) : null}
                <div className="company-actions-inline" style={{ marginTop: 12 }}>
                  {connected ? (
                    <button type="button" className="btn btn-secondary" onClick={() => { void disconnect(); }} disabled={disconnecting}>
                      {disconnecting ? t('savingEllipsis', { ns: 'admin' }) : t('whatsappAppDisconnect', { ns: 'companies' })}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-primary" onClick={() => { void reconnect(); }} disabled={refreshingQr}>
                      {refreshingQr ? t('savingEllipsis', { ns: 'admin' }) : t('whatsappAppReconnect', { ns: 'companies' })}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div style={{ textAlign: 'center' }}>
            {qrSrc && !connected ? (
              <img
                src={qrSrc}
                alt={t('whatsappAppQrAlt', { ns: 'companies' })}
                style={{ width: 220, height: 220, background: '#fff', padding: 8, borderRadius: 12 }}
              />
            ) : connected ? (
              <div style={{ padding: 24, borderRadius: 12, background: '#e8f7ee', color: '#146c2e', fontWeight: 700 }}>
                {t('whatsappAppConnected', { ns: 'companies' })}
              </div>
            ) : (
              <div style={{ padding: 24, color: '#516173' }}>{t('whatsappAppWaitingQr', { ns: 'companies' })}</div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
};
